import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, imageUrl, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useLocalized } from "../lib/localized";
import { useCategories } from "../lib/useCategories";
import useDocumentSEO from "../lib/useDocumentSEO";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import CTABand from "../components/site/CTABand";
import FadeIn from "../components/site/FadeIn";
import Lightbox from "../components/site/Lightbox";
import BackToTop from "../components/site/BackToTop";

function paragraphs(text) {
  if (!text) return [];
  return text
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const lp = useLocalized();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    setError(null);
    Promise.all([
      api.get(`/projects/by-slug/${slug}`),
      api.get("/site-content"),
      api.get("/projects", { params: { status: "published" } }),
    ])
      .then(([pRes, cRes, listRes]) => {
        if (cancelled) return;
        setProject(pRes.data);
        setContent(cRes.data);
        setAllProjects(listRes.data);
        window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
      })
      .catch((e) =>
        !cancelled &&
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Not found")
      );
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Compute SEO values (safe for null project)
  const filters = content?.projects_section?.filters || {};
  const { categories } = useCategories();
  const categoryLabel = project
    ? (() => {
        const c = categories.find((x) => x.slug === project.category);
        return c ? t(c.name) : (t(filters[project.category]) || project.category);
      })()
    : "";
  const seoTitle = project
    ? `${t(project.title || project.name)} — Cipta Andalan`
    : "Cipta Andalan";
  const seoDesc = project
    ? (t(project.summary) || (t(project.description) || "").slice(0, 160) ||
        `${categoryLabel} · ${project.location} · ${project.year}`)
    : "";
  const ogImage = project?.cover_image
    ? (project.cover_image.startsWith("http")
        ? project.cover_image
        : `${process.env.REACT_APP_BACKEND_URL}${project.cover_image.startsWith("/api/") ? project.cover_image : "/api/files/" + project.cover_image}`)
    : "";
  useDocumentSEO({ title: seoTitle, description: seoDesc, ogImage, lang });

  if (error) {
    return (
      <div className="bg-white min-h-screen">
        <Nav content={content} />
        <div className="container-x pt-40 pb-32 text-center text-[#5d5f64]">
          {lang === "id" ? "Proyek tidak ditemukan." : "Project not found."}
          <div className="mt-8">
            <Link to={lp("/proyek", "/en/projects")} className="text-[#16181c] underline">
              {lang === "id" ? "Kembali ke proyek" : "Back to projects"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!project || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#5d5f64]">
        Loading…
      </div>
    );
  }

  const idx = allProjects.findIndex((p) => p.slug === project.slug);
  const nextProject = idx >= 0 ? allProjects[(idx + 1) % allProjects.length] : null;

  // Compose a flat gallery: cover (if any) first, then all gallery entries
  const galleryItems = [];
  if (project.cover_image) {
    galleryItems.push({
      path: project.cover_image,
      caption: { id: t(project.title), en: t(project.title) },
    });
  }
  (project.gallery || []).forEach((g) => galleryItems.push(g));

  return (
    <div className="bg-white">
      <Nav content={content} />
      <main>
        {/* Full-bleed cover with title overlay */}
        <section
          className="relative w-full bg-[#1b1c1e] text-white overflow-hidden"
          style={{ minHeight: "92vh" }}
          data-testid="project-cover"
        >
          {project.cover_image && (
            <img
              src={imageUrl(project.cover_image)}
              alt={t(project.title || project.name) + " — " + categoryLabel}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-75"
              aria-hidden
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1b1c1e]/95 via-[#1b1c1e]/40 to-transparent" />
          <div className="relative container-x pt-32 pb-14 md:pt-40 md:pb-20 min-h-[92vh] flex flex-col justify-end">
            <Link
              to={lp("/proyek", "/en/projects")}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-10 w-fit"
              data-testid="project-back-top"
            >
              <ArrowLeft size={14} /> {lang === "id" ? "Semua proyek" : "All projects"}
            </Link>
            <div className="eyebrow text-white/60 mb-6" data-testid="project-eyebrow">
              {categoryLabel}
            </div>
            <h1
              className="font-display font-bold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em] max-w-[22ch]"
              data-testid="project-title"
            >
              {t(project.title || project.name)}
            </h1>
            <div
              className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[14px] text-white/75 border-t border-white/15 pt-6"
              data-testid="project-meta"
            >
              <span>
                <span className="text-white/45 mr-2">{lang === "id" ? "Kategori" : "Category"}</span>
                {categoryLabel}
              </span>
              <span>
                <span className="text-white/45 mr-2">{lang === "id" ? "Lokasi" : "Location"}</span>
                {project.location}
              </span>
              <span>
                <span className="text-white/45 mr-2">{lang === "id" ? "Tahun" : "Year"}</span>
                {project.year}
              </span>
              {project.client && (
                <span data-testid="project-meta-client">
                  <span className="text-white/45 mr-2">{lang === "id" ? "Klien" : "Client"}</span>
                  {project.client}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Body: scope + description */}
        <section className="container-x py-24 md:py-32" data-testid="project-body">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
              <div className="md:col-span-4 space-y-8">
                {t(project.scope) && (
                  <div data-testid="project-scope">
                    <div className="eyebrow mb-3">{lang === "id" ? "Lingkup" : "Scope"}</div>
                    <div className="text-[#16181c] text-base md:text-[17px] leading-relaxed">
                      {t(project.scope)}
                    </div>
                  </div>
                )}
                <div>
                  <div className="eyebrow mb-3">{lang === "id" ? "Tentang" : "About"}</div>
                  <div className="text-[#5d5f64] text-sm">
                    {project.location} · {project.year}
                    {project.client ? ` · ${project.client}` : ""}
                  </div>
                </div>
              </div>
              <div className="md:col-span-8 space-y-6">
                {paragraphs(t(project.description)).map((para, i) => (
                  <p
                    key={i}
                    className="text-[#16181c] text-lg md:text-xl leading-[1.55] max-w-[60ch]"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </FadeIn>
        </section>

        {/* Gallery */}
        {galleryItems.length > 0 && (
          <section
            className="container-x pb-24 md:pb-32 hairline-t pt-16 md:pt-20"
            data-testid="project-gallery"
          >
            <FadeIn>
              <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
                <div>
                  <div className="eyebrow mb-3">{lang === "id" ? "Galeri" : "Gallery"}</div>
                  <h2 className="font-display font-semibold text-[#16181c] text-3xl md:text-4xl tracking-[-0.02em]">
                    {lang === "id" ? "Dokumentasi proyek" : "Project documentation"}
                  </h2>
                </div>
                <div className="text-sm text-[#5d5f64]">
                  {galleryItems.length} {lang === "id" ? "gambar" : "images"}
                </div>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {galleryItems.map((g, i) => (
                <FadeIn key={i} delay={Math.min(i, 5) * 50}>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full aspect-[4/3] bg-[#1b1c1e] overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#16181c] focus:ring-offset-2 rounded-2xl"
                    data-testid={`project-gallery-thumb-${i}`}
                    aria-label={t(g.caption) || `${t(project.title)} — ${i + 1}`}
                  >
                    <img
                      src={imageUrl(g.path)}
                      alt={t(g.caption) || `${t(project.title)} — image ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                    />
                  </button>
                  {t(g.caption) && (
                    <div className="mt-3 text-xs text-[#5d5f64]">{t(g.caption)}</div>
                  )}
                </FadeIn>
              ))}
            </div>
          </section>
        )}

        {/* Next project + back to all */}
        <section className="container-x pb-24 md:pb-32 hairline-t pt-12 md:pt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <Link
              to={lp("/proyek", "/en/projects")}
              className="group block"
              data-testid="project-back-link"
            >
              <div className="eyebrow mb-3">{lang === "id" ? "Kembali" : "Back"}</div>
              <div className="font-display font-semibold text-[#16181c] text-2xl md:text-3xl tracking-[-0.01em] inline-flex items-center gap-3">
                <ArrowLeft size={20} />
                {lang === "id" ? "Kembali ke proyek" : "Back to projects"}
              </div>
            </Link>
            {nextProject && nextProject.slug !== project.slug && (
              <Link
                to={lp(`/proyek/${nextProject.slug}`, `/en/projects/${nextProject.slug}`)}
                className="group block md:text-right"
                data-testid="project-next-link"
              >
                <div className="eyebrow mb-3">
                  {lang === "id" ? "Proyek berikutnya" : "Next project"}
                </div>
                <div className="font-display font-semibold text-[#16181c] text-2xl md:text-3xl tracking-[-0.01em] inline-flex items-center gap-3">
                  {t(nextProject.title || nextProject.name)}
                  <ArrowRight size={20} />
                </div>
                <div className="text-sm text-[#5d5f64] mt-2">
                  {nextProject.location} · {nextProject.year}
                </div>
              </Link>
            )}
          </div>
        </section>

        <CTABand content={content} />
      </main>
      <Footer content={content} />

      {lightboxIndex >= 0 && (
        <Lightbox
          images={galleryItems}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
      <BackToTop />
    </div>
  );
}